import { ElasticCourseData, ElasticMatchplayMatchesMatchesInner, TournamentEventTypeEnum, TournamentRequestFourpeopleTypesEnum } from '@spikemark/rest-api';
import mapValues from 'lodash/mapValues';
import sum from 'lodash/sum';
import { ReactNode } from 'react';
import { LeaderboardDisplayMode } from '../../types/leaderboard';
import { cn } from '../../utils/cn';
import { displayHoleResult, displayScoreResult } from '../../utils/format';
import { isSubstitute } from '../../utils/player';
import { TableColumn, TableRowOptions } from '../table/types';
import { MatchplayCellHeader } from './matchplay-cell-header';
import { MatchplayDeltaIcon } from './matchplay-delta-icon';
import { ScorecardCourseLegend } from './scorecard-course-legend';
import { ScorecardRow } from './types';
const SCORE_WIDTH = 40;
type GenerateColumnsForHolesOptions = {
  from: number;
  to: number;
  holes?: number[];
  selector: (i: number) => (rowData: ScorecardRow) => any;
  cell?: (i: number) => (options: TableRowOptions<ScorecardRow>) => ReactNode;
  courseInfoMap: Record<string, ElasticCourseData>;
  roundId: string;
  format: TournamentEventTypeEnum;
  matchplayFormat?: TournamentRequestFourpeopleTypesEnum;
  match?: ElasticMatchplayMatchesMatchesInner;
};
const renderParAndYardage = (par: number, yardage: number) => <p data-sentry-component="renderParAndYardage" data-sentry-source-file="scorecard-columns.tsx">
    {yardage}
    <br />
    {par}
  </p>;
const renderCourseInfo = (courseInfo: ElasticCourseData, holeIndex: number) => renderParAndYardage(courseInfo.pars?.[holeIndex], courseInfo.yards?.[holeIndex]);
function mapCourseInfo(courseInfoMap: Record<string, ElasticCourseData>, holeIndex: number) {
  return mapValues(courseInfoMap, courseInfo => renderCourseInfo(courseInfo, holeIndex));
}
function generateColumnsForHoles(options: GenerateColumnsForHolesOptions) {
  let columns: TableColumn<ScorecardRow>[] = [];
  const {
    from,
    to,
    selector,
    cell,
    courseInfoMap,
    match
  } = options;
  for (let i = from - 1; i < to; i++) {
    const holeId = options.holes?.[i] || i + 1;
    const column: TableColumn<ScorecardRow> = {
      header: options.format === 'Stroke Play' ? holeId : <MatchplayCellHeader holeIndex={i} match={match} />,
      subHeader: <ScorecardCourseLegend childrenByCourseLabel={mapCourseInfo(courseInfoMap, i)} />,
      sortIteratee: selector(i),
      headerCellClassName: 'numeric',
      cellClassName: ({
        rowData
      }) => cn('numeric', {
        'bg-amber-300/30': rowData.rounds?.find(x => String(x.roundId) === options.roundId)?.startingHole === holeId
      }),
      width: SCORE_WIDTH,
      cellRenderer: cell ? cell(i) : undefined,
      rowSpan: ({
        rowData,
        rowIndex
      }) => {
        if (rowData.type === 'MATCHPLAY_DELTA') {
          return 1;
        }

        // Crude, but it works!
        if (options.matchplayFormat === 'FOURSOMES') {
          return [0, 3].includes(rowIndex) ? 2 : 0;
        }
        return 1;
      }
    };
    columns.push(column);
  }
  return columns;
}
export const createScorecardRowColumns = ({
  mode,
  roundId,
  courseInfoMap,
  scoresCount,
  format,
  matchplayFormat,
  holes,
  rowCount,
  match
}: {
  mode: LeaderboardDisplayMode;
  roundId: string;
  courseInfoMap: Record<string, ElasticCourseData>;
  scoresCount?: number;
  format: TournamentEventTypeEnum;
  matchplayFormat?: TournamentRequestFourpeopleTypesEnum;
  holes?: number[];
  rowCount: number;
  match?: ElasticMatchplayMatchesMatchesInner;
}) => {
  const roundIdAsNumber = parseInt(roundId, 10);
  const roundIndex = roundIdAsNumber - 1;
  const totalColumnSettings = {
    cellClassName: 'numeric border-l border-r px-2 lg:px-3',
    width: SCORE_WIDTH,
    hidden: format === 'Match Play'
  };
  const SCORE_THRESHOLD = 10000;
  let columns: TableColumn<ScorecardRow>[] = [];
  const inOutCellRenderer = (start: number, end: number, overrideMode: LeaderboardDisplayMode = mode) => ({
    rowData: row
  }: TableRowOptions<ScorecardRow>) => {
    if (row.type === 'MATCHPLAY_DELTA' || isSubstitute(row, roundIdAsNumber)) {
      return '';
    } else if (!row.playerId) {
      return <span className="text-secondary">-</span>;
    } else {
      const round = row.rounds?.find(round => String(round.roundId) == String(roundId));
      const roundScore = row.scores?.[roundIndex] ?? 0;
      const scores = sum((round?.scores ?? []).slice(start, end).filter(x => typeof x === 'number'));
      const strokes = sum((round?.strokes ?? []).slice(start, end).filter(x => typeof x === 'number'));
      const par = (strokes ?? 0) - (scores ?? 0);
      return roundScore >= SCORE_THRESHOLD ? 'X' : displayScoreResult({
        strokes,
        par
      }, overrideMode);
    }
  };
  const toParColumn: TableColumn<ScorecardRow> = {
    header: 'Today',
    sortIteratee: row => row.scores.length ? sum(row.scores) : 1000,
    cellRenderer: inOutCellRenderer(0, 18, 'score'),
    ...totalColumnSettings,
    cellClassName: ({
      rowIndex
    }) => cn(totalColumnSettings.cellClassName, {
      'border-r-4': typeof scoresCount === 'number',
      'border-birdie-red': typeof scoresCount === 'number' && rowIndex < scoresCount,
      'border-over-par-gray': typeof scoresCount === 'number' && rowIndex >= scoresCount
    }),
    headerCellClassName: totalColumnSettings.cellClassName,
    width: 50
  };
  columns.push(toParColumn);
  const scoresSelector = (i: number) => (row: ScorecardRow) => row.scores[i];
  const cellFormat = (i: number) => ({
    rowData: row
  }: {
    rowData: ScorecardRow;
  }) => {
    const {
      pars
    } = courseInfoMap?.[`${roundId}-${row.courseLabels?.[roundIndex] ?? 'A'}`] ?? {};
    const round = row.rounds?.find(round => String(round.roundId) == String(roundId));
    const roundScore = row.scores?.[roundIndex] ?? 0;
    if (row.type === 'MATCHPLAY_DELTA') {
      return <MatchplayDeltaIcon rowData={row} holeIndex={i} />;
    }
    if (row.playerId) {
      if (isSubstitute(row, roundIdAsNumber)) {
        return '';
      }
      if (roundScore >= SCORE_THRESHOLD) {
        return 'X';
      }
      const par = pars?.[i];
      const strokes = round?.strokes?.[i];
      return typeof strokes === 'number' ? strokes === 0 ? 'X' : displayHoleResult({
        strokes,
        par
      }, mode, format !== 'Stroke Play') : '-';
    } else {
      return <span>-</span>;
    }
  };
  columns = columns.concat(generateColumnsForHoles({
    from: 1,
    to: 9,
    selector: scoresSelector,
    cell: cellFormat,
    courseInfoMap,
    roundId,
    holes,
    format,
    matchplayFormat,
    match
  }));
  const outColumn: TableColumn<ScorecardRow> = {
    header: 'OUT',
    sortIteratee: row => row.scores.length ? sum(row.scores.slice(0, 9)) : 100,
    cellRenderer: inOutCellRenderer(0, 9),
    subHeader: <ScorecardCourseLegend childrenByCourseLabel={mapValues(courseInfoMap, courseInfo => renderParAndYardage(sum(courseInfo.pars?.slice(0, 9)), sum(courseInfo.yards?.slice(0, 9))))} />,
    ...totalColumnSettings
  };
  columns.push(outColumn);
  columns = columns.concat(generateColumnsForHoles({
    from: 10,
    to: 18,
    selector: scoresSelector,
    cell: cellFormat,
    courseInfoMap,
    roundId,
    holes,
    format,
    matchplayFormat,
    match
  }));
  const inColumn: TableColumn<ScorecardRow> = {
    header: 'IN',
    sortIteratee: row => row.scores.length ? sum(row.scores.slice(9, 18)) : 100,
    cellRenderer: inOutCellRenderer(9, 18),
    subHeader: <ScorecardCourseLegend childrenByCourseLabel={mapValues(courseInfoMap, courseInfo => renderParAndYardage(sum(courseInfo.pars?.slice(9, 18)), sum(courseInfo.yards?.slice(9, 18))))} />,
    ...totalColumnSettings
  };
  columns.push(inColumn);
  const rdColumn: TableColumn<ScorecardRow> = {
    header: 'RD',
    sortIteratee: row => row.scores.length ? sum(row.scores) : 1000,
    cellRenderer: inOutCellRenderer(0, 18),
    subHeader: <ScorecardCourseLegend childrenByCourseLabel={mapValues(courseInfoMap, courseInfo => renderParAndYardage(sum(courseInfo.pars), sum(courseInfo.yards)))} />,
    ...totalColumnSettings
  };
  columns.push(rdColumn);
  if (holes && holes.length > 18) {
    columns.push({
      headerCellClassName: 'bg-gray-300',
      cellClassName: 'bg-gray-300 max-w-6 !px-0 !py-0',
      cellRenderer: () => <div className="-rotate-90 uppercase text-coolgray-dark text-xs font-bold text-center w-24 translate-x-[-36px]">
          Playoff
        </div>,
      rowSpan: ({
        rowIndex
      }) => rowIndex === 0 ? rowCount : 0,
      width: '1%'
    });
    columns = columns.concat(generateColumnsForHoles({
      from: 19,
      to: holes?.length,
      selector: scoresSelector,
      cell: cellFormat,
      courseInfoMap,
      roundId,
      holes,
      format,
      matchplayFormat,
      match
    }));
  }
  return columns;
};